<template>
  <div class="relative redemption-report">
    <div v-if="error" class="error text-center">
      {{ error }}
    </div>
    <h1 class="page-header text-center text-3xl">redemption report</h1>
    <div class="text-center mb-2">
      analyze how members are redeeming rewards
    </div>
    <div class="flex justify-center items-center absolute top-2 right-5">
      <div v-if="downloadReportLoading" class="flex justify-center items-center w-40 mt-3">
        <loading-icon />
      </div>
      <template v-else>
        <a v-if="downloadUrl" :href="downloadUrl" class="button" download>
          download report
        </a>
        <button
          v-else
          :class="{ disabled: !reportId }"
          class="button"
          @click="generateExport"
        >
          download report
        </button>
      </template>
    </div>
    <DateRange
      :start.sync="startDate"
      :end.sync="endDate"
      :max="maxDate"
      :min="startDate"
      format="MM/dd/yyyy"
      @update:range="updateRange"
      class="w-52 mx-auto font-primary text-md"
    />
    <button
      class="button w-28 mx-auto"
      @click="runReport"
    >
      run report
    </button>
    <div class="flex justify-center items-center w-full text-center my-6">
      <div class="mx-12">
        <div v-if="loadingReport" class="w-full flex justify-center items-center m-auto loading-icon">
          <loading-icon />
        </div>
        <template v-else-if="totalPointsRedeemed || totalPointsRedeemed === 0">
          <div class="text-lg font-primary">total points redeemed</div>
          <div class="text-2xl">{{totalPointsRedeemed}}</div>
        </template>
      </div>
      <div class="mx-12">
        <div v-if="loadingReport" class="w-full flex justify-center items-center m-auto loading-icon">
          <loading-icon />
        </div>
        <template v-else-if="uniqueMembersRedeemed || uniqueMembersRedeemed === 0">
          <div class="text-lg font-primary">unique members redeemed</div>
          <div class="text-2xl">{{uniqueMembersRedeemed}}</div>
        </template>
      </div>
      <div class="mx-12">
        <div v-if="loadingReport" class="w-full flex justify-center items-center m-auto loading-icon">
          <loading-icon />
        </div>
        <template v-else-if="rewardsRedeemed || rewardsRedeemed === 0">
          <div class="text-lg font-primary">rewards redeemed</div>
          <div class="text-2xl">{{rewardsRedeemed}}</div>
        </template>
      </div>
    </div>
    <div v-if="loadingReport" class="flex justify-center align-center mt-12">
      <loading-icon />
    </div>
    <div v-else-if="reportLoaded" class="mt-12">
      <sb-custom-table
        :rows="tableRows"
        :columns="tableColumns"
        :defaultSort="sortDirection"
        @sortChange="updateTableSort"
        disableTableSort
        sortable
      />
      <div v-if="sortLoading" class="flex justify-center items-center m-auto w-full">
        <loading-icon />
      </div>
      <div v-else-if="pagesTotal > 1" class="flex justify-center items-center mt-10">
        <pagination
          :page.sync="page"
          :pages="pagesTotal"
          :gap="2"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { subDays } from 'date-fns';
import { DateRange, SbCustomTable, Pagination, LoadingIcon } from '@springbig/sb-components';
import { ReportAPI } from '../../services/api/Reports';

export default {
  name: "RedemptionReport",
  components: {
    DateRange,
    SbCustomTable,
    Pagination,
    LoadingIcon,
  },
  data() {
    return {
      startDate: new Date(subDays(new Date(), 89)),
      endDate: new Date(),
      maxDate: new Date(),
      rewardsRedeemed: "",
      totalPointsRedeemed: "",
      uniqueMembersRedeemed: "",
      downloadUrl: "",
      page: 0,
      sortColumn: 0,
      sortDirection: "desc",
      pagesTotal: 1,
      reportId: null,
      tableRows: [],
      reportLoaded: false,
      loadingReport: false,
      downloadReportLoading: false,
      sortLoading: false,
      error: null,
      tableColumns: [
        { key: "reward_value", value: "reward value" },
        { key: "reward_name", value: "reward name" },
        { key: "num_of_redemptions", value: "number of \n redemptions" },
        { key: "points_used", value: "points \n used" },
        { key: "percent_of_total", value: "percentage of total \n points redeemed" }
      ],
    }
  },
  watch: {
    async page(value) {
      this.loadingReport = true;
      await this.fetchReportData(value + 1);
      this.loadingReport = false;
    }
  },
  methods: {
    handleError(error) {
      this.error = error;
    },
    async runReport() {
      if (this.startDate && this.endDate) {
        this.error = null;
        this.loadingReport = true;
        await this.generateReport(this.startDate, this.endDate);
        await this.fetchReportData(1, this.sortColumn, this.sortDirection);
        this.loadingReport = false;
      }
      else {
        this.error = 'please select a start and end date';
      }
    },
    async fetchReportData(page, columnIndex, direction) {
      try {
        const reportRes = await ReportAPI.getReportById(this.reportId, page, { columnIndex, direction });
        const { results } = reportRes.data.report.output_data;
        this.pagesTotal = reportRes.data.pagination.total_pages;
        if (results.length > 0) {
          this.tableRows = results.map(result => ({
            ...result,
            points_used: parseInt(result.points_used),
            percent_of_total: `${parseInt(result.percent_of_total)}%`,
          }));
          this.reportLoaded = true;
        }
      } catch(error) {
        this.handleError(error);
      }
    },
    async generateReport(startDate, endDate) {
      this.page = 0;
      this.error = null;
      try {
        const res = await ReportAPI.createReport(
          'Reports::RedemptionsReport',
          startDate,
          endDate,
        );
        const { report } = res.data;
        this.rewardsRedeemed = report.output_data.rewards_redeemed,
        this.totalPointsRedeemed = parseInt(report.output_data.total_points_redeemed),
        this.uniqueMembersRedeemed = report.output_data.unique_members_redeemed,
        this.reportId = res.data.report?.id;
      } catch (error) {
        this.handleError(error);
      }
    },
    async checkDownloadUrlStatus() {
      try {
        const res = await ReportAPI.getReportDownloadUrl(this.reportId);
        if (res.data.report.download_url) {
          this.downloadReportLoading = false;
          this.downloadUrl = res.data.report.download_url;

          // Download file right after generation is done
          const link = document.createElement('a');
          link.href = this.downloadUrl;
          link.download = true;
          link.click();
          link.remove();
        }
        else if(res.data.report.export_state === 'failed') {
          this.handleError(new Error('report generation failed. please try again.'));
          this.downloadReportLoading = false;
        }
        else {
          setTimeout(this.checkDownloadUrlStatus, 1000);
        }
      }
      catch(error) {
        this.handleError(error);
        this.downloadReportLoading = false;
      }
    },
    generateExport() {
      if (!this.downloadUrl) {
        this.downloadReportLoading = true;
        ReportAPI.generateExportByReportId(this.reportId);
        setTimeout(this.checkDownloadUrlStatus, 1000);
      }
    },
    async updateTableSort(sort) {
      const index = this.tableColumns.findIndex(column => column.key === sort.column);
      if (
        (this.sortColumn !== index ||
        this.sortDirection !== sort.sorting) &&
        this.tableRows.length > 0
      ) {
        this.tableRows = [];
        this.sortLoading = true;
        this.sortColumn = index;
        this.sortDirection = sort.sorting;
        await this.fetchReportData(this.page + 1, index, sort.sorting);
        this.sortLoading = false;
      }
    },
    async updateRange(value) {
      this.startDate = value.start;
      this.endDate = value.end;
      if (value.start && value.end) {
        this.loadingReport = true;
        await this.generateReport(value.start, value.end);
        await this.fetchReportData(this.page + 1);
        this.loadingReport = false;
      }
      else {
        this.error = 'please select a start and end date';
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.redemption-report {
  .page-header {
    margin: 0;
  }
  .loading-icon {
    width: 35px!important;
  }
  .sb-input {
    padding: 0;
    color: #007aff;
  }
  .button {
    display: block;
    margin: 10px auto 0 auto;
    height: 30px;
    font-size: 1rem;
    padding: 5px 20px;
    color: rgba(255, 255, 255, 0.7);
    border-radius: 5px;
  }
  .sb-table__head-item {
    white-space: pre-line!important;
    margin-left: 4px;
    .top-0 {
      top: 50%!important;
      transform: translateY(-50%);
    }
    .sb-table__head-item__arrow {
      line-height: 6px!important;
    }
  }
}
</style>
